// Calls to sydon-api — the shared identity/Amazon-connection service STAC reuses
// instead of reimplementing PropelAuth verification or a second Amazon OAuth flow.
import { SYDON_API_URL } from '../config/env';

interface SydonApiEnvelope<T> {
  data: T;
}

interface SydonApiErrorBody {
  message?: string;
  error?: string;
  errors?: Array<{ message?: string }>;
}

async function sydonApiErrorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as SydonApiErrorBody;
    const detail = body.message ?? body.error ?? body.errors?.[0]?.message;
    if (detail) return detail;
  } catch {
    // response wasn't JSON — fall through to the status-only message
  }
  return `sydon-api request failed: ${res.status}`;
}

async function sydonApiRequest<T>(
  path: string,
  accessToken: string | null,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${SYDON_API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });
  if (!res.ok) throw new Error(await sydonApiErrorMessage(res));
  if (res.status === 204) return undefined as unknown as T;
  const body = (await res.json()) as SydonApiEnvelope<T>;
  return body.data;
}

export interface SydonUser {
  id: string;
  organization_id: string;
  role: string;
  email: string;
}

export interface RegisterPayload {
  email: string;
  password: string;
  confirm_password: string;
  organization_name: string;
  first_name: string;
  last_name: string;
}

export const sydonAuthApi = {
  validate: (accessToken: string): Promise<{ user: SydonUser }> =>
    sydonApiRequest('/api/v1/auth/validate', accessToken, { method: 'POST' }),

  // No accessToken yet — this creates the account, it doesn't start a session.
  // STAC's session always comes from the PropelAuth hosted login (LoginGate),
  // so after this succeeds the user logs in the normal way, they aren't
  // auto-signed-in from whatever register() returns.
  register: (payload: RegisterPayload): Promise<void> =>
    sydonApiRequest('/api/v1/auth/register', null, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

export interface AmazonAccount {
  seller_id: string;
  marketplace_id: string;
}

export const sydonAmazonApi = {
  getAccounts: (accessToken: string): Promise<AmazonAccount[]> =>
    sydonApiRequest('/api/v1/amazon/accounts', accessToken),

  getAuthUrl: (accessToken: string): Promise<{ authUrl: string }> =>
    sydonApiRequest('/api/v1/amazon/amazonAuthUrl', accessToken),
};
